/**
 * Completion_Service — the pure, shared "% Completion" SSOT
 * (application-completion-tracker spec, design §C1).
 *
 * A single, synchronous, I/O-free function both surfaces consume — the admin
 * `% Completion` column (via `useParticipationCompletion`) and the contestant
 * `CompletionBar` (via `useApplicationCompletion`) — so for the same
 * `(applicant, competition, division)` they can never disagree (Requirement 1).
 *
 * OVERALL denominator (Decision D1, overridden during review per product
 * direction): the metric answers "how much of the application is completed
 * **overall**", NOT merely "is it submittable". The denominator therefore counts
 * ALL division-visible fields the applicant can fill — required AND optional —
 * plus ALL in-scope Document_Catalog entries — required AND optional. 100% means
 * the whole application is filled in, not that it merely clears the submit gate.
 *
 * Counted_Fields (denominator):
 *   - ALL division-visible CUSTOM fields on visible pages (required + optional),
 *     using the same page-visible AND field-visible `isVisibleToApplicant`
 *     predicate the submit gate applies.
 *   - PLUS ALL in-scope Document_Catalog entries (required + optional), using the
 *     same scope logic as the submit path: `entriesInScope(...)` when the
 *     applicant type resolves, and the null/'BOTH' + division-visible fallback
 *     when it does not.
 *
 * Answered (numerator):
 *   - CUSTOM fields whose stored value passes the shared, pure `isAnswered`
 *     predicate re-exported from `fieldValidation.ts` (the same emptiness rule
 *     the submit gate uses — 0 and false count as answered; blank text/empty
 *     lists/empty file sets do not).
 *   - PLUS in-scope catalog entries whose `id` appears among the uploaded
 *     documents' `catalogEntryId` set.
 *
 * Every step is pure and total: the function never mutates its input and never
 * throws for a well-typed argument (Property 10).
 *
 * Validates: Requirements 1.4, 1.5, 1.6, 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7,
 *            3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 4.1, 4.2, 4.3, 4.4, 7.4, 8.3
 */

import {
  isAnswered,
  type FormVersionSnapshot,
  type ResponseValue,
  type SnapshotPage,
  type FormFieldDef,
} from '../../../../infrastructure/lambda/application-form/fieldValidation';
import {
  entriesInScope,
  type DocumentCatalogEntry,
} from '../../../../infrastructure/lambda/application-form/documentCatalog';
import {
  backCompatMapping,
  type ApplicantType,
} from '../../../../infrastructure/lambda/division-registry/resolution';

/** UNAVAILABLE ⇒ the completion source could not be resolved (Requirement 4.3, 8). */
export type AvailabilityState = 'AVAILABLE' | 'UNAVAILABLE';

/** The output consumed by both surfaces (Glossary: Completion_Result). */
export interface CompletionResult {
  /** Integer 0..100 inclusive; meaningful only when availability === 'AVAILABLE'. */
  percent: number;
  /** Answered_Count (Counted_Fields answered/provided). */
  answeredCount: number;
  /** Total_Count (size of Counted_Fields = all visible fields + in-scope docs). */
  totalCount: number;
  availability: AvailabilityState;
}

/** All inputs are already-fetched, plain data — the function performs no I/O. */
export interface CompletionInput {
  /** Active FormVersion snapshot, or null when none could be resolved. */
  snapshot: FormVersionSnapshot | null;
  /** The applicant's CUSTOM answers (ApplicationResponse.values); {} when none. */
  values: Record<string, ResponseValue>;
  /** The applicant's division for this competition; '' when not yet chosen. */
  divisionKey: string;
  /** Per-competition Document_Catalog; [] when none. */
  documentCatalog: DocumentCatalogEntry[];
  /** The applicant's uploads (needs catalogEntryId to match the gate); [] when none. */
  uploadedDocuments: { catalogEntryId: string }[];
}

/** The UNAVAILABLE result — a single shared constant so consumers compare cheaply. */
const UNAVAILABLE_RESULT: CompletionResult = {
  percent: 0,
  answeredCount: 0,
  totalCount: 0,
  availability: 'UNAVAILABLE',
};

/**
 * Whether a `visibleDivisionKeys` selection means "all divisions" — null,
 * undefined, or only blank entries (the legacy `BOTH` semantics). Mirrors the
 * private `isAllDivisionsVisibility` in `fieldValidation.ts` so the denominator's
 * visibility predicate is identical to the numerator gate's (Property 3).
 */
function isAllDivisionsVisibility(
  visibleDivisionKeys: readonly string[] | null | undefined,
): boolean {
  if (visibleDivisionKeys == null) {
    return true;
  }
  return visibleDivisionKeys.every((k) => typeof k !== 'string' || k.trim() === '');
}

/**
 * Whether an element carrying `visibleDivisionKeys` is visible to an applicant
 * assigned to `divisionKey`. Identical to the private `isVisibleToApplicant` the
 * submit gate applies, so the fields counted in the denominator are exactly the
 * division-visible fields the applicant can fill.
 */
function isVisibleToApplicant(
  visibleDivisionKeys: readonly string[] | null | undefined,
  divisionKey: string,
): boolean {
  if (isAllDivisionsVisibility(visibleDivisionKeys)) {
    return true;
  }
  return (visibleDivisionKeys as readonly string[]).some(
    (k) => typeof k === 'string' && k.trim() !== '' && k === divisionKey,
  );
}

/**
 * Reverse the Back-Compat mapping to derive the applicant type from a division
 * key: `mrs` → MRS, `miss` → MISS, anything else → undefined. This mirrors the
 * exact reverse-lookup `ApplyRendererSurface` performs, so the completion gate
 * scopes documents the same way the apply surface's own gate does. When
 * undefined (a division with no legacy applicant type) only all-applicant-type
 * (null/'BOTH') entries are counted — the same fallback
 * `ApplicationFormContainer.computeMissingDocuments` applies.
 */
function applicantTypeForDivision(divisionKey: string): ApplicantType | undefined {
  if (!divisionKey) return undefined;
  return (Object.keys(backCompatMapping) as ApplicantType[]).find(
    (type) => backCompatMapping[type] === divisionKey,
  );
}

/**
 * ALL in-scope document catalog entries for the applicant (required AND
 * optional). When `applicantType` is resolved, this is exactly
 * `entriesInScope(...)`. When it is undefined, it mirrors
 * `computeMissingDocuments`'s fallback scope: only entries scoped to all
 * applicant types (null/'BOTH') AND in the applicant's division scope are
 * counted, since type-specific entries cannot be asserted without the type.
 * Pure; never mutates the catalog.
 */
function inScopeDocuments(
  catalog: DocumentCatalogEntry[],
  divisionKey: string,
  applicantType: ApplicantType | undefined,
): DocumentCatalogEntry[] {
  if (applicantType !== undefined) {
    return entriesInScope([...catalog], divisionKey, applicantType);
  }
  return catalog.filter(
    (entry) =>
      (entry.applicantTypeScope == null || entry.applicantTypeScope === 'BOTH') &&
      isVisibleToApplicant(entry.visibleDivisionKeys, divisionKey),
  );
}

/** All division-visible CUSTOM field ids, required AND optional (Counted_Fields, custom part). */
function countedVisibleCustomFieldIds(
  snapshot: FormVersionSnapshot,
  divisionKey: string,
): Set<string> {
  const ids = new Set<string>();
  for (const page of snapshot.pages as SnapshotPage[]) {
    const pageVisible = isVisibleToApplicant(page.visibleDivisionKeys, divisionKey);
    if (!pageVisible) continue;
    for (const field of page.fields as FormFieldDef[]) {
      if (field.fieldCategory !== 'CUSTOM') continue;
      if (!isVisibleToApplicant(field.visibleDivisionKeys, divisionKey)) continue;
      ids.add(field.id);
    }
  }
  return ids;
}

/**
 * Round to the nearest integer then apply the reservation rules of Requirement
 * 3: 100 is reserved for a fully-complete application and 0 for one with no
 * answered fields, so a partial application is always in `[1, 99]`. The result
 * is always clamped to `[0, 100]`.
 */
function percentFromCounts(answeredCount: number, totalCount: number): number {
  if (totalCount === 0) {
    // Empty form is complete (Requirement 4.1).
    return 100;
  }
  if (answeredCount <= 0) {
    return 0; // reservation: 0 iff nothing answered (Requirement 3.3)
  }
  if (answeredCount >= totalCount) {
    return 100; // reservation: 100 iff fully complete (Requirement 3.4)
  }
  const raw = (answeredCount / totalCount) * 100;
  let percent = Math.round(raw);
  // A partial application never rounds to the reserved endpoints (3.5, 3.6).
  if (percent >= 100) percent = 99;
  if (percent <= 0) percent = 1;
  return percent;
}

/**
 * Compute the shared Completion_Result for one applicant/participation.
 * Pure, synchronous, total: same input ⇒ same output, no I/O, never throws for
 * a well-typed argument, never mutates its input.
 */
export function computeCompletion(input: CompletionInput): CompletionResult {
  const { snapshot, values, divisionKey, documentCatalog, uploadedDocuments } = input;

  // 1 — Availability. No active FormVersion ⇒ UNAVAILABLE (Requirement 4.3, 8).
  if (snapshot == null) {
    return UNAVAILABLE_RESULT;
  }

  const applicantType = applicantTypeForDivision(divisionKey);

  // 2 — Counted_Fields (denominator, OVERALL: all visible fields + in-scope docs).
  const countedCustomIds = countedVisibleCustomFieldIds(snapshot, divisionKey);
  const inScopeDocs = inScopeDocuments(documentCatalog, divisionKey, applicantType);
  const totalCount = countedCustomIds.size + inScopeDocs.length;

  // 3 — Answered_Count (numerator).
  //   a) Answered CUSTOM fields = counted field ids whose stored value passes
  //      the shared, pure `isAnswered` predicate (the same emptiness rule the
  //      submit gate applies), counting both required and optional fields.
  let answeredCustom = 0;
  for (const id of countedCustomIds) {
    if (isAnswered(values[id])) answeredCustom += 1;
  }

  //   b) Provided documents = in-scope catalog entries with a matching upload
  //      (matched by catalogEntryId), counting both required and optional docs.
  const uploadedIds = new Set(uploadedDocuments.map((u) => u.catalogEntryId));
  const providedDocs = inScopeDocs.filter((entry) => uploadedIds.has(entry.id)).length;

  const answeredCount = answeredCustom + providedDocs;

  // 4 — Percent + rounding + reservation rules (Requirement 3, 4).
  return {
    percent: percentFromCounts(answeredCount, totalCount),
    answeredCount: totalCount === 0 ? 0 : answeredCount,
    totalCount,
    availability: 'AVAILABLE',
  };
}

export default computeCompletion;
